/**
 * FormVersionSelector — sélecteur optionnel d'une version de formulaire PUBLIÉE
 * à lier à un intake_link (9.U.5, magic-link versionné).
 *
 * « Aucun formulaire — lien classique » par défaut. Ne liste que les templates
 * ayant une dernière version publiée identifiée par le serveur. À la sélection d'un
 * formulaire, la DERNIÈRE version publiée est choisie par défaut ; les versions
 * publiées plus anciennes restent sélectionnables. Loading/empty/error non
 * bloquants pour le flow legacy (le lien reste créable sans formulaire).
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useFormTemplates, useFormTemplateVersions } from "../../../lib/queries/formTemplates";

type Props = {
  /** `form_template_version_id` sélectionné, ou `undefined` = lien classique. */
  value: string | undefined;
  onChange: (versionId: string | undefined) => void;
  disabled?: boolean;
};

const selectCls =
  "mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:bg-slate-50 disabled:text-slate-500";

export function FormVersionSelector({ value, onChange, disabled }: Props) {
  const { t } = useTranslation();
  const [templateId, setTemplateId] = useState<string>("");

  const templatesQ = useFormTemplates({ status: "active" });
  // Seuls les modèles dont la dernière version publiée est identifiée sont sélectionnables.
  const publishedTemplates = useMemo(
    () => (templatesQ.data?.items ?? []).filter((tpl) => Boolean(tpl.latest_published_version_id)),
    [templatesQ.data],
  );

  const versionsQ = useFormTemplateVersions(templateId || undefined);
  const publishedVersions = useMemo(
    () =>
      (versionsQ.data?.items ?? [])
        .filter((v) => v.published_at !== null)
        .sort((a, b) => b.version_number - a.version_number),
    [versionsQ.data],
  );

  const onPickTemplate = (id: string) => {
    setTemplateId(id);
    // La liste fournit déjà la dernière version publiée : le choix affiché
    // et le payload doivent être identiques, même avant le chargement des versions.
    onChange(
      publishedTemplates.find((tpl) => tpl.id === id)?.latest_published_version_id ?? undefined,
    );
  };

  return (
    <fieldset className="space-y-2">
      <legend className="block text-sm font-medium text-slate-700">
        {t("intake_links.create.form.label")}
      </legend>

      {templatesQ.isLoading && !templatesQ.data ? (
        <p className="text-xs text-slate-500" aria-live="polite">
          {t("app.loading")}
        </p>
      ) : templatesQ.error ? (
        <p className="text-xs text-slate-500">{t("intake_links.create.form.error")}</p>
      ) : publishedTemplates.length === 0 ? (
        <p className="text-xs text-slate-500">{t("intake_links.create.form.empty")}</p>
      ) : (
        <>
          <select
            aria-label={t("intake_links.create.form.template_label")}
            value={templateId}
            disabled={disabled}
            onChange={(e) => onPickTemplate(e.target.value)}
            className={selectCls}
          >
            <option value="">{t("intake_links.create.form.none")}</option>
            {publishedTemplates.map((tpl) => (
              <option key={tpl.id} value={tpl.id}>
                {tpl.name}
              </option>
            ))}
          </select>

          {templateId && publishedVersions.length > 0 && (
            <select
              aria-label={t("intake_links.create.form.version_label")}
              value={value ?? ""}
              disabled={disabled}
              onChange={(e) => onChange(e.target.value)}
              className={selectCls}
            >
              {publishedVersions.map((v) => (
                <option key={v.id} value={v.id}>
                  {t("intake_links.create.form.version_option", { number: v.version_number })}
                </option>
              ))}
            </select>
          )}
        </>
      )}
      <p className="text-xs text-slate-500">{t("intake_links.create.form.help")}</p>
    </fieldset>
  );
}
